// Tiny visual-token module shared by all People Insights primitives.
// Centralising the accent palette keeps the page visually cohesive and
// makes future re-skinning a single-file change.

export type AccentToken =
  | 'indigo'
  | 'emerald'
  | 'amber'
  | 'cyan'
  | 'rose'
  | 'violet';

export interface AccentSpec {
  /** Saturated colour, used for graph bars / icons / progress fills. */
  main: string;
  /** Light surface tint, used as background for icon chips and tiles. */
  surface: string;
  /** Text colour used on top of `surface`. */
  on: string;
}

export const ACCENTS: Record<AccentToken, AccentSpec> = {
  indigo: { main: '#4F46E5', surface: '#EEF2FF', on: '#3730A3' },
  emerald: { main: '#10B981', surface: '#ECFDF5', on: '#047857' },
  amber: { main: '#F59E0B', surface: '#FFFBEB', on: '#B45309' },
  cyan: { main: '#06B6D4', surface: '#ECFEFF', on: '#0E7490' },
  rose: { main: '#F43F5E', surface: '#FFF1F2', on: '#BE123C' },
  violet: { main: '#8B5CF6', surface: '#F5F3FF', on: '#6D28D9' },
};

/**
 * Deterministically pick an accent for an arbitrary string label.
 * Used for breakdown rows (BU, pillar, …) so colours are stable across
 * renders and don't need to be hand-mapped.
 *
 * Note: with only 6 colours this collides often — for lists where the rows
 * are already ordered (BUs by headcount, pillars by share, …) prefer
 * `colorByIndex` which guarantees a wider, less-collision-prone palette
 * and adjacent rows that look distinct.
 */
export function accentForLabel(label: string): AccentToken {
  const order: AccentToken[] = ['indigo', 'emerald', 'amber', 'cyan', 'rose', 'violet'];
  let h = 0;
  for (let i = 0; i < label.length; i += 1) {
    h = (h * 31 + label.charCodeAt(i)) >>> 0;
  }
  return order[h % order.length];
}

// ---------------------------------------------------------------------------
// Extended chart palette used by bar lists (Top BUs, Pillars, …).
//
// Hand-tuned so adjacent indices are visibly distinct in both hue and
// luminance — important when rendering thin horizontal bars stacked on top
// of each other. Picked from Tailwind's 500 shade where possible so the
// colours sit comfortably next to ACCENTS above.
// ---------------------------------------------------------------------------

export const CHART_PALETTE: readonly string[] = [
  '#4F46E5', // indigo
  '#F59E0B', // amber
  '#10B981', // emerald
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#8B5CF6', // violet
  '#F43F5E', // rose
  '#22C55E', // green
  '#3B82F6', // blue
  '#EAB308', // yellow
  '#14B8A6', // teal
  '#A855F7', // purple
];

/**
 * Pick a chart bar colour by position in a sorted list. Cycles through the
 * extended palette — so a list of N rows gets `palette[0..N-1 % palette.len]`.
 *
 * This is the right tool when you want adjacent bars to look distinct and
 * don't need colour-by-identity (e.g. ranked Top-N lists).
 */
export function colorByIndex(index: number): string {
  if (!Number.isFinite(index) || index < 0) return CHART_PALETTE[0];
  return CHART_PALETTE[index % CHART_PALETTE.length];
}
